<template>
  <transition name="kqd-fade">
    <div v-if="isOpen" class="kqd-overlay" @mousedown.self="close">
      <div class="kqd-dialog" role="dialog" aria-modal="true">
        <!-- Header -->
        <div class="kqd-header">
          <div class="kqd-header__left">
            <div class="kqd-header__agent-picker" @click.stop="agentDropdownOpen = !agentDropdownOpen">
              <div class="kqd-header__avatar">
                <img v-if="selectedAgentUUID === 'prota'" :src="protaAvatar" class="kqd-header__avatar-img" alt="Prota" />
                <img v-else-if="selectedAgentUUID === 'proka'" :src="prokaAvatar" class="kqd-header__avatar-img" alt="Proka" />
                <cortex-icon v-else :size="14"></cortex-icon>
              </div>
              <span class="kqd-header__title">{{ selectedAgent ? selectedAgent.name : 'Kortex' }}</span>
              <i :class="['kqd-header__chevron fas', agentDropdownOpen ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
              <div v-if="agentDropdownOpen" class="kqd-agent-menu" @click.stop>
                <div
                  v-for="agent in agents"
                  :key="agent.uuid"
                  :class="['kqd-agent-option', selectedAgentUUID === agent.uuid ? 'kqd-agent-option--active' : '']"
                  @click="onAgentChange(agent.uuid)"
                >
                  <img v-if="agent.uuid === 'prota'" :src="protaAvatar" class="kqd-agent-option__avatar" alt="Prota" />
                  <img v-else-if="agent.uuid === 'proka'" :src="prokaAvatar" class="kqd-agent-option__avatar" alt="Proka" />
                  <div v-else class="kqd-agent-option__icon"><i class="fas fa-robot"></i></div>
                  <span class="kqd-agent-option__name">{{ agent.name }}</span>
                  <i v-if="selectedAgentUUID === agent.uuid" class="fas fa-check kqd-agent-option__check"></i>
                </div>
              </div>
            </div>
            <span v-if="activeTitle" class="kqd-header__conv">— {{ activeTitle }}</span>
          </div>
          <div class="kqd-header__actions">
            <button class="kqd-icon-btn" :title="$t('kortex.quickDialogOpenSidebar')" @click="moveToSidebar">
              <i class="fas fa-columns"></i>
              <span class="kqd-icon-btn__label">{{ $t('kortex.quickDialogOpenSidebar') }}</span>
            </button>
            <button class="kqd-icon-btn kqd-icon-btn--close" :title="$t('close')" @click="close">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Messages -->
        <div ref="messages" class="kqd-messages">
          <template v-if="recentMessages.length > 0">
            <template v-for="(msg, idx) in recentMessages">
              <message-bubble
                v-if="msg.role === 'user' || (msg.role === 'assistant' && (msg.content || msg.thinking || msg.isError || !isStreaming))"
                :key="msg.uuid || idx"
                :message="msg"
              ></message-bubble>
            </template>
            <typing-indicator
              v-if="(isStreaming && !streamingContent && !streamingThinking) || isOCRLoading"
              :using-tools="streamingUsingTools"
            ></typing-indicator>
            <tool-approval-card v-if="pendingToolApproval"></tool-approval-card>
            <div v-if="isStreaming" class="kqd-stop-bar">
              <button class="kqd-stop-btn" @click="stopGeneration">
                <i class="fas fa-stop-circle"></i>
                {{ $t('kortex.stop') }}
              </button>
            </div>
          </template>
          <div v-else class="kqd-welcome">
            <div class="kqd-welcome__orb">
              <cortex-icon :size="26"></cortex-icon>
            </div>
            <p class="kqd-welcome__text">{{ $t('kortex.welcomeMessage') }}</p>
            <div v-if="pageTools.length > 0" class="kqd-welcome__caps">
              <div class="kqd-welcome__caps-label">{{ $t('kortex.pageCapabilities') }}</div>
              <div class="kqd-welcome__caps-list">
                <div v-for="tool in (toolsExpanded ? pageTools : pageTools.slice(0, 3))" :key="tool.function.name" class="kqd-welcome__cap-chip">
                  <i class="fas fa-bolt"></i>
                  <span>{{ tool.function.description }}</span>
                </div>
              </div>
              <button v-if="pageTools.length > 3" class="kqd-welcome__caps-toggle" @click="toolsExpanded = !toolsExpanded">
                <i :class="toolsExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                {{ toolsExpanded ? $t('kortex.showLess') : `+${pageTools.length - 3} ${$t('kortex.more')}` }}
              </button>
            </div>
          </div>
        </div>

        <!-- Input -->
        <div class="kqd-input-wrap">
          <!-- OCR error -->
          <div v-if="ocrError" class="kqd-ocr-error">
            <i class="fas fa-exclamation-triangle"></i>
            {{ ocrError }}
            <button class="kqd-ocr-error__close" @click="ocrError = null"><i class="fas fa-times"></i></button>
          </div>
          <div :class="['kqd-input-box', isStreaming ? 'kqd-input-box--disabled' : '']">
            <input ref="ocrInput" type="file" accept="image/*,.pdf" class="kqd-file-input" @change="onOCRFileSelected" />
            <div class="kqd-attach-menu" @click.stop>
              <button
                class="kqd-attach-btn"
                :disabled="isStreaming"
                :title="$t('kortex.addAttachment')"
                @click="attachMenuOpen = !attachMenuOpen"
              >
                <i class="fas fa-plus"></i>
              </button>
              <div v-if="attachMenuOpen" class="kqd-attach-menu__popup">
                <button
                  v-if="canOCR"
                  class="kqd-attach-menu__item"
                  :disabled="isOCRLoading"
                  @click="triggerOCRUpload(); attachMenuOpen = false"
                >
                  <i :class="isOCRLoading ? 'fas fa-spinner fa-spin' : 'fas fa-eye'"></i>
                  <span>{{ $t('kortex.ocrButton') }}</span>
                </button>
              </div>
            </div>
            <textarea
              ref="input"
              v-model="inputText"
              :placeholder="$t('kortex.placeholder')"
              :disabled="isStreaming"
              class="kqd-textarea"
              rows="1"
              @keydown.enter.exact.prevent="send"
              @keydown.esc.prevent="close"
              @input="autoResize"
            ></textarea>
            <button
              v-if="isStreaming"
              class="kqd-action-btn kqd-action-btn--stop"
              @click="stopGeneration"
            >
              <i class="fas fa-stop"></i>
            </button>
            <button
              v-else
              class="kqd-action-btn"
              :disabled="!inputText.trim()"
              @click="send"
            >
              <i class="fas fa-arrow-up"></i>
            </button>
          </div>
          <div class="kqd-footer">
            <span class="kqd-footer__hint">Esc to close &nbsp;·&nbsp; Enter to send &nbsp;·&nbsp; Shift+Enter new line &nbsp;·&nbsp; ⌘⇧K to toggle</span>
            <button class="kqd-open-full" @click="moveToSidebar">
              <i class="fas fa-expand-alt"></i>
              {{ $t('kortex.quickDialogOpenSidebar') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import MessageBubble from './MessageBubble'
import TypingIndicator from './TypingIndicator'
import ToolApprovalCard from './ToolApprovalCard'
import { getPageToolSchemas } from '@/helpers/cortexTools'
var protaAvatar = require('@/assets/images/prota-avatar.png')
var prokaAvatar = require('@/assets/images/proka-avatar.png')

var MAX_QUICK_MESSAGES = 20

export default {
  name: 'KortexQuickDialog',
  components: { MessageBubble, TypingIndicator, ToolApprovalCard },
  data: function () {
    return {
      inputText: '',
      agentDropdownOpen: false,
      attachMenuOpen: false,
      ocrError: null,
      protaAvatar: protaAvatar,
      prokaAvatar: prokaAvatar,
      pageTools: [],
      toolsExpanded: false,

    }
  },
  computed: {
    isOpen: function () {
      return this.$store.state.cortex.isQuickDialogOpen
    },
    messages: function () {
      return this.$store.state.cortex.messages
    },
    recentMessages: function () {
      var msgs = this.messages
      if (msgs.length <= MAX_QUICK_MESSAGES) return msgs
      return msgs.slice(msgs.length - MAX_QUICK_MESSAGES)
    },
    isStreaming: function () {
      return this.$store.state.cortex.isStreaming
    },
    isOCRLoading: function () {
      return this.$store.state.cortex.isOCRLoading
    },
    canOCR: function () {
      var tier = this.$store.state.cortex.tier
      return tier === 'standard' || tier === 'pro'
    },
    streamingContent: function () {
      return this.$store.state.cortex.streamingContent
    },
    streamingThinking: function () {
      return this.$store.state.cortex.streamingThinking
    },
    streamingUsingTools: function () {
      var msgs = this.$store.state.cortex.messages
      for (var i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant') {
          return !!(msgs[i].tool_calls && msgs[i].tool_calls.length > 0 && !msgs[i].content)
        }
      }
      return false
    },
    pendingToolApproval: function () {
      return this.$store.state.cortex.pendingToolApproval
    },
    activeTitle: function () {
      var self = this
      var uuid = self.$store.state.cortex.activeConversationUUID
      if (!uuid) return null
      var conv = self.$store.state.cortex.conversations.find(function (c) {
        return c.uuid === uuid
      })
      if (conv && conv.title) return conv.title
      return null
    },
    agents: function () {
      return this.$store.state.cortex.agents
    },
    selectedAgentUUID: function () {
      return this.$store.state.cortex.selectedAgentUUID
    },
    selectedAgent: function () {
      var self = this
      if (!self.selectedAgentUUID) return null
      return self.agents.find(function (a) { return a.uuid === self.selectedAgentUUID }) || null
    }
  },
  watch: {
    isOpen: function (val) {
      if (val) {
        this.$nextTick(function () {
          this.scrollToBottom()
          if (this.$refs.input) this.$refs.input.focus()
        }.bind(this))
      }
    },
    messages: function () {
      this.$nextTick(this.scrollToBottom)
    },
    isStreaming: function (val) {
      if (!val) {
        this.$nextTick(this.scrollToBottom)
      }
    },
    pendingToolApproval: function () {
      this.$nextTick(this.scrollToBottom)
    }
  },
  mounted: function () {
    document.addEventListener('keydown', this.onKeydown)
    document.addEventListener('click', this.closeAttachMenu)
    window.addEventListener('kortex:capabilities-changed', this._refreshCapabilities)
    this._refreshCapabilities()
  },
  beforeDestroy: function () {
    document.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('click', this.closeAttachMenu)
    window.removeEventListener('kortex:capabilities-changed', this._refreshCapabilities)
  },
  methods: {
    _refreshCapabilities: function () {
      this.pageTools = getPageToolSchemas()
    },
    onKeydown: function (e) {
      if (e.key === 'Escape' && this.isOpen) {
        if (this.agentDropdownOpen) {
          this.agentDropdownOpen = false
          return
        }
        e.stopPropagation()
        this.close()
      }
    },
    onAgentChange: function (uuid) {
      this.agentDropdownOpen = false
      this.$store.commit('cortex/SET_SELECTED_AGENT', uuid)
    },
    close: function () {
      this.$store.dispatch('cortex/closeQuickDialog')
    },
    moveToSidebar: function () {
      this.$store.dispatch('cortex/closeQuickDialog')
      this.$store.dispatch('cortex/openChat')
    },
    send: function () {
      var text = this.inputText.trim()
      if (!text || this.isStreaming) return
      this.inputText = ''
      this.$nextTick(function () {
        if (this.$refs.input) this.$refs.input.style.height = 'auto'
      }.bind(this))
      this.$store.dispatch('cortex/sendMessage', text)
    },
    triggerOCRUpload: function () {
      this.$refs.ocrInput.click()
    },
    onOCRFileSelected: function (e) {
      var file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      var self = this
      var reader = new FileReader()
      reader.onload = function (ev) {
        self.$store.dispatch('cortex/injectOCRResult', {
          fileName: file.name,
          fileData: ev.target.result,
          contentType: file.type
        }).then(function () {
          self.ocrError = null
        }).catch(function (err) {
          if (err && err.message === 'ocr_tier') {
            self.ocrError = self.$t('kortex.ocrTierError')
          } else {
            self.ocrError = self.$t('kortex.ocrError')
          }
        })
      }
      reader.readAsDataURL(file)
    },
    closeAttachMenu: function () {
      this.attachMenuOpen = false
    },
    stopGeneration: function () {
      this.$store.dispatch('cortex/stopGeneration')
    },
    autoResize: function () {
      var el = this.$refs.input
      if (!el) return
      el.style.height = 'auto'
      el.style.height = Math.min(el.scrollHeight, 120) + 'px'
    },
    scrollToBottom: function () {
      var el = this.$refs.messages
      if (el) el.scrollTop = el.scrollHeight
    }
  }
}
</script>

<style scoped lang="scss">
.kqd-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 80px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(3px);
}

.kqd-dialog {
  width: 660px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 120px);
  background: #1a1d27;
  border: 1px solid rgba(108, 128, 255, 0.2);
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(108, 128, 255, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Header ── */
.kqd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
}

.kqd-header__left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.kqd-header__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.kqd-header__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.kqd-header__agent-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  position: relative;
  padding: 2px 4px;
  border-radius: 8px;
  transition: background 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
}

.kqd-header__chevron {
  font-size: 10px;
  color: var(--text-secondary, #8899aa);
}

.kqd-agent-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 6px;
  min-width: 200px;
  background: #1a1d2e;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  z-index: 100;
  padding: 6px;
}

.kqd-agent-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }

  &--active {
    background: rgba(108, 128, 255, 0.1);
  }
}

.kqd-agent-option__avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.kqd-agent-option__icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(108, 128, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(168, 183, 255, 0.6);
  font-size: 11px;
  flex-shrink: 0;
}

.kqd-agent-option__name {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary, #e2e8f0);
  flex: 1;
}

.kqd-agent-option__check {
  font-size: 11px;
  color: #6c80ff;
}

.kqd-header__title {
  font-weight: 600;
  font-size: 14px;
  color: var(--text-primary, #e2e8f0);
  flex-shrink: 0;
}

.kqd-header__conv {
  font-size: 13px;
  color: var(--text-secondary, #8899aa);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kqd-header__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.kqd-icon-btn {
  background: none;
  border: none;
  color: var(--text-secondary, #8899aa);
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    color: var(--text-primary, #e2e8f0);
  }

  &__label {
    font-size: 12px;
    white-space: nowrap;
  }

  &--close:hover {
    background: rgba(239, 68, 68, 0.12);
    color: #ef4444;
  }
}

/* ── Messages ── */
.kqd-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 120px;
  max-height: 380px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(108, 128, 255, 0.2);
    border-radius: 2px;
  }
}

.kqd-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  gap: 12px;
  text-align: center;
}

.kqd-welcome__orb {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  background: rgba(108, 128, 255, 0.1);
  border: 1px solid rgba(108, 128, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.kqd-welcome__text {
  font-size: 13px;
  color: var(--text-secondary, #8899aa);
  max-width: 320px;
  line-height: 1.5;
  margin: 0;
}

.kqd-welcome__caps {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  width: 100%;
}

.kqd-welcome__caps-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.2);
}

.kqd-welcome__caps-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.kqd-welcome__caps-toggle {
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.25);
  padding: 2px 4px;
  margin-top: 1px;
  transition: color 0.15s;

  &:hover {
    color: rgba(255, 255, 255, 0.5);
  }

  i {
    font-size: 8px;
  }
}

.kqd-welcome__cap-chip {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 7px;
  padding: 6px 9px;
  text-align: left;

  i {
    font-size: 9px;
    color: #6c80ff;
    margin-top: 2px;
    flex-shrink: 0;
  }

  span {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.38);
    line-height: 1.5;
  }
}

/* ── Input ── */
.kqd-input-wrap {
  padding: 12px 14px 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
}

.kqd-input-box {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 8px 10px 8px 14px;
  transition: border-color 0.15s;

  &:focus-within {
    border-color: rgba(108, 128, 255, 0.4);
  }

  &--disabled {
    opacity: 0.7;
  }
}

.kqd-textarea {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: var(--text-primary, #e2e8f0);
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  min-height: 22px;
  max-height: 120px;
  overflow-y: auto;

  &::placeholder {
    color: var(--text-secondary, #8899aa);
  }
}

.kqd-action-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
  transition: background 0.15s, opacity 0.15s;
  background: linear-gradient(135deg, #6c80ff, #764ba2);
  color: #fff;

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &--stop {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.25);
  }
}

/* ── Stop button in message area ── */
.kqd-stop-bar {
  display: flex;
  justify-content: center;
  padding: 8px 0 4px;
}

.kqd-stop-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid rgba(220, 70, 70, 0.35);
  background: rgba(220, 70, 70, 0.1);
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.18s;

  i {
    font-size: 12px;
    color: #e05252;
  }

  &:hover {
    background: rgba(220, 70, 70, 0.2);
    border-color: rgba(220, 70, 70, 0.55);
    color: #fff;
  }

  &:active {
    transform: scale(0.97);
  }
}

/* ── Footer ── */
.kqd-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
}

.kqd-footer__hint {
  font-size: 11px;
  color: var(--text-muted, #5a6680);
}

.kqd-open-full {
  background: none;
  border: none;
  font-size: 11px;
  color: rgba(108, 128, 255, 0.7);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  transition: color 0.15s;

  &:hover {
    color: #6c80ff;
  }
}

/* ── Transition ── */
.kqd-fade-enter-active,
.kqd-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.kqd-fade-enter,
.kqd-fade-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(-8px);
}

/* ── File input (hidden) ── */
.kqd-file-input {
  display: none;
}

/* ── Attach menu ── */
.kqd-attach-menu {
  position: relative;
  flex-shrink: 0;

  &__popup {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 0;
    background: #1e1e2e;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 180px;
    z-index: 100;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 7px;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.75);
    font-size: 13px;
    cursor: pointer;
    text-align: left;
    width: 100%;
    transition: background 0.12s, color 0.12s;

    i { width: 16px; text-align: center; font-size: 14px; }

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.07);
      color: #fff;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
}

.kqd-attach-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.15s;
  padding: 0;
  margin-right: 2px;

  &:hover:not(:disabled) {
    color: rgba(255, 255, 255, 0.7);
  }

  &:disabled {
    opacity: 0.25;
    cursor: not-allowed;
  }
}

/* ── Image previews ── */
.kqd-image-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 12px 0;
}

.kqd-image-thumb {
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__remove {
    position: absolute;
    top: 3px;
    right: 3px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.7);
    border: none;
    color: rgba(255, 255, 255, 0.8);
    font-size: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;

    &:hover { background: rgba(200, 60, 60, 0.8); }
  }
}

/* ── OCR error banner ── */
.kqd-ocr-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 0;
  padding: 8px 10px;
  background: rgba(220, 60, 60, 0.12);
  border: 1px solid rgba(220, 60, 60, 0.3);
  border-radius: 8px;
  color: rgba(255, 120, 120, 0.9);
  font-size: 12px;

  &__close {
    margin-left: auto;
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 0;
    font-size: 11px;
    opacity: 0.7;
    &:hover { opacity: 1; }
  }
}
</style>
